import { Goal, initialDateFormat, Timestamp } from '@griply/types';
import {
  formatFullDate,
  getStartTillEndLabel,
  getWeekStartsOn,
} from '@griply/utils';
import { isBefore, isSameDay } from 'date-fns';
import { t } from 'i18next';
import React, { useMemo, useRef } from 'react';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { useTheme } from 'styled-components';

import * as Styled from './goal-list-card.style';

export type StartDateCellProps = {
  goal: Goal;
};

export const StartDateCell: React.FC<StartDateCellProps> = ({ goal }) => {
  const theme = useTheme();
  const today = useToday();
  const user = useUser();
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
  const startDateRef = useRef<HTMLSpanElement>(null);
  const startDateButtonRef = useRef<HTMLSpanElement>(null);
  const {
    menuOpen: startDateMenuOpen,
    openMenu: openStartDateMenu,
    closeMenu: closeStartDateMenu,
  } = useOpenMenu();

  const started =
    !goal.startDate ||
    isBefore(goal.startDate, today) ||
    isSameDay(goal.startDate, today);

  const startDateLabel = useMemo(
    () =>
      goal.startDate
        ? getStartTillEndLabel({
            startDate: goal.startDate,
            isShort: true,
            today,
            t,
          })
        : undefined,
    [goal.startDate, today],
  );

  const onStartDateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openStartDateMenu();
  };

  const onStartDateChange = (date?: Timestamp) => {
    updateGoal({ goal: { id: goal.id, startDate: date ?? null } });
    closeStartDateMenu();
  };

  return (
    <>
      <Styled.DataContainer
        ref={startDateRef}
        $type={Styled.DataTypes.StartDate}
        $isFrozen={!!goal.frozenAt}
        $isClickable={!goal.frozenAt}
        onClick={!goal.frozenAt ? onStartDateClick : undefined}
      >
        {startDateLabel ? (
          <Styled.DataButton
            ref={startDateButtonRef}
            $color={!started ? theme.palette.text.secondary : undefined}
          >
            {startDateLabel}
          </Styled.DataButton>
        ) : (
          <Styled.SelectPlaceholder $forceShow={startDateMenuOpen}>
            {t('general.labels.select')}
          </Styled.SelectPlaceholder>
        )}
      </Styled.DataContainer>

      {!!goal.startDate && (
        <Tooltip
          referenceElement={startDateButtonRef}
          label={formatFullDate(goal.startDate, dateFormat)}
        />
      )}

      {startDateMenuOpen && (
        <DatepickerWithOptions
          referenceElement={startDateRef}
          value={goal.startDate ?? undefined}
          onChange={onStartDateChange}
          dateFormat={dateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          icon={IconOptions.Calendar}
          canRemoveValue={!!goal.startDate}
          onOutside={closeStartDateMenu}
        />
      )}
    </>
  );
};
